/* RGB reusable multi-buy selector. It only changes Shopify's existing quantity field and visible price feedback. */
(() => {
  const parseVariantMap = (selector) => {
    try {
      return JSON.parse(selector.querySelector('[data-rgb-multibuy-variants]')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  };

  const initBridge = (bridge) => {
    if (!bridge || bridge.dataset.rgbMultibuyBound === 'true') return;
    const selector = bridge.querySelector('[data-rgb-multibuy]');
    if (!selector) return;

    const customRoot = document.querySelector('product-info.prismtide-page');
    const genericRoot = document.querySelector('product-info[id^="MainProduct-"]');
    const root = customRoot || genericRoot;
    if (!root) return;

    const quantityWrap = root.querySelector('.product-form__quantity');
    const quantityInput = quantityWrap?.querySelector('input[name="quantity"]');
    const productForm = root.querySelector('product-form form[data-type="add-to-cart-form"]');
    const variantInput = productForm?.querySelector('input[name="id"]');
    const submitButton = productForm?.querySelector('button[type="submit"][name="add"]');
    const submitText = submitButton?.querySelector('span');
    const purchaseRow = root.querySelector('.pt-purchase-row');
    const genericBuyButtons = root.querySelector('.product-form__buttons');
    const priceRoot = root.querySelector('.pt-price') || root.querySelector('[id^="price-"]');

    if (!quantityWrap || !quantityInput || !productForm || !variantInput || !submitButton || !submitText || !priceRoot) return;

    bridge.dataset.rgbMultibuyBound = 'true';
    const variantMap = parseVariantMap(selector);
    let compareMap = {};
    try { compareMap = JSON.parse(selector.querySelector('[data-rgb-multibuy-compares]')?.textContent || '{}'); }
    catch (_) { compareMap = {}; }
    const currency = selector.dataset.currency || window.Shopify?.currency?.active || 'GBP';
    const mode = selector.dataset.mode || 'percent';
    const fixedTwo = Number.parseInt(selector.dataset.fixedTwo || '0', 10) || 0;
    const fixedThree = Number.parseInt(selector.dataset.fixedThree || '0', 10) || 0;
    const fixedFour = Number.parseInt(selector.dataset.fixedFour || '0', 10) || 0;
    const twoMultiplier = Number.parseInt(selector.dataset.twoMultiplier || '900', 10) || 900;
    const threeMultiplier = Number.parseInt(selector.dataset.threeMultiplier || '850', 10) || 850;
    const fourMultiplier = Number.parseInt(selector.dataset.fourMultiplier || '800', 10) || 800;
    const maxQty = Number.parseInt(selector.dataset.maxQty || '3', 10) || 3;
    const productLabel = selector.dataset.productLabel || 'item';
    const productPlural = selector.dataset.productPlural || `${productLabel}s`;
    const defaultButtonText = submitText.textContent.trim() || 'Add to cart';

    const formatMoney = (cents) => {
      const amount = Number(cents) / 100;
      try {
        const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(amount);
        const originalText = (priceRoot.textContent || '');
        return /\b[A-Z]{3}\b/.test(originalText) && !formatted.includes(currency) ? `${formatted} ${currency}` : formatted;
      } catch (_) {
        return `£${amount.toFixed(2)}${currency === 'GBP' ? ' GBP' : ''}`;
      }
    };

    const getCurrentVariantCents = () => {
      const liveVariantInput = root.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]') || variantInput;
      const id = String(liveVariantInput?.value || '');
      const mapped = Number.parseInt(variantMap[id], 10);
      return Number.isSafeInteger(mapped) && mapped > 0 ? mapped : 0;
    };

    const normalizePriceEnding = (cents) => {
      const ending = currency === 'GBP' ? 99 : currency === 'USD' ? 95 : null;
      if (ending === null) return cents;
      const base = Math.floor(cents / 100) * 100;
      const high = base + ending;
      const low = high - 100;
      if (low > 0 && Math.abs(cents - low) < Math.abs(high - cents)) return low;
      return high;
    };

    const getTotalCents = (qty, unitCents) => {
      if (qty === 1) return unitCents;
      if (mode === 'regular' || mode === 'shopify_cart_offer') return unitCents * qty;
      // AuraRise and PrismTide: the LIVE Shopify offer is 15% off exactly
      // ONE second unit, at most once per order. Never invent quantity tiers
      // or round a legitimate discount to an arbitrary .95/.99 ending.
      // Estimate uses integer cents; Shopify determines final allocations.
      if (mode === 'shopify_bxgy_once') {
        const discountOnce = Math.round(unitCents * 15 / 100);
        return unitCents * qty - discountOnce;
      }
      if (qty > maxQty) return unitCents * qty;
      let total;
      if (mode === 'fixed') {
        if (qty === 2 && fixedTwo > 0) total = fixedTwo;
        if (qty === 3 && fixedThree > 0) total = fixedThree;
        if (qty === 4 && fixedFour > 0) total = fixedFour;
      }
      if (!total) {
        const regular = unitCents * qty;
        const multiplier = qty === 2 ? twoMultiplier : qty === 3 ? threeMultiplier : fourMultiplier;
        total = Math.floor((regular * multiplier) / 1000);
      }
      return total; // Always exact Shopify-aligned cents; no invented price endings.
    };

    const visiblePriceItem = () => {
      const price = priceRoot.querySelector('.price');
      if (!price) return priceRoot.querySelector('.price-item');
      if (price.classList.contains('price--on-sale')) {
        return priceRoot.querySelector('.price__sale .price-item--last') || priceRoot.querySelector('.price__sale .price-item');
      }
      return priceRoot.querySelector('.price__regular .price-item--regular') || priceRoot.querySelector('.price-item--last') || priceRoot.querySelector('.price-item');
    };

    const updateCards = (unitCents) => {
      selector.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const qty = Number.parseInt(radio.value, 10);
        const card = radio.closest('.rgb-multibuy__option');
        if (!card || qty < 1 || qty > maxQty) return;
        const total = getTotalCents(qty, unitCents);
        const regular = unitCents * qty;
        const each = Math.floor(total / qty);
        const totalEl = card.querySelector('[data-rgb-total]');
        const eachEl = card.querySelector('[data-rgb-each]');
        const compareEl = card.querySelector('[data-rgb-compare]');
        const saveEl = card.querySelector('[data-rgb-save]');
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (compareEl) {
          compareEl.textContent = formatMoney(regular);
          compareEl.hidden = mode === 'regular' || mode === 'shopify_cart_offer' || regular === total;
        }
        if (eachEl) eachEl.textContent = qty === 1 ? `${formatMoney(unitCents)} each` : `≈ ${formatMoney(each)} each`;
        if (saveEl) {
          if (mode === 'regular') saveEl.textContent = 'At the regular price';
          else if (mode === 'shopify_cart_offer') saveEl.textContent = 'Eligible savings calculated in cart';
          else if (mode === 'shopify_bxgy_once') saveEl.textContent = '2nd item 15% off · Save ' + formatMoney(Math.max(0, regular - total));
          else saveEl.textContent = 'Save ' + formatMoney(Math.max(0, regular - total));
        }
      });
    };

    // Headline price represents the selected quantity/deal total.
    // Real variant unit prices and cart quantities are never modified here.
    const updateVisiblePrice = (qty, unitCents) => {
      const target = root.querySelector('.pt-price') || root.querySelector('[id^="price-"]');
      const priceNode = target?.querySelector('.price');
      if (!target || !priceNode || !unitCents) return;
      const amount = Math.max(1, qty);
      const total = getTotalCents(amount, unitCents);
      const full = amount * unitCents;
      const idInput = root.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]') || variantInput;
      const unitCompare = Number.parseInt(compareMap[String(idInput?.value)] || 0, 10) || 0;
      const bundleSaving = total < full;
      const priceSale = unitCompare > unitCents;
      const showSale = bundleSaving || priceSale;
      const reference = bundleSaving ? full : unitCompare * amount;
      const regular = target.querySelector('.price__regular .price-item--regular');
      const sale = target.querySelector('.price__sale .price-item--last');
      const crossed = target.querySelector('.price__sale s.price-item--regular');
      if (regular) regular.textContent = formatMoney(total);
      if (sale) sale.textContent = formatMoney(total);
      if (crossed && showSale) crossed.textContent = formatMoney(reference);
      priceNode.classList.toggle('price--on-sale', showSale);
      target.dataset.rgbMultibuyQty = String(amount);
      target.dataset.rgbMultibuyTotal = String(total);

      let caption = target.querySelector('[data-rgb-multibuy-headline-caption]');
      if (!caption) {
        caption = document.createElement('p');
        caption.className = 'rgb-multibuy__headline-caption';
        caption.setAttribute('data-rgb-multibuy-headline-caption', '');
        target.appendChild(caption);
      }
      caption.hidden = amount === 1;
      if (amount > 1) {
        const units = mode === 'fixed' && productLabel.includes('2-pack')
          ? `${amount} packs · ${amount * 2} lights`
          : `${amount} ${productPlural}`;
        const estimated = ['fixed', 'shopify_bxgy_once', 'shopify_cart_offer'].includes(mode);
        caption.textContent = `${estimated ? 'Estimated ' : ''}total for ${units} · ${formatMoney(unitCents)} per ${productLabel}${estimated ? ' · Final savings confirmed in cart' : ''}`;
      } else {
        caption.textContent = '';
      }
    };

    const selectedQty = () => Number.parseInt(selector.querySelector('input[type="radio"]:checked')?.value || '1', 10) || 1;
    let syncingQuantity = false;
    const applySelection = (qty, announce = true, origin = 'deal') => {
      if (!Number.isSafeInteger(qty) || qty < 1) return;
      const unitCents = getCurrentVariantCents();
      if (!unitCents) return;
      const liveQuantityInput = root.querySelector('.product-form__quantity input[name="quantity"]') || quantityInput;
      syncingQuantity = true;
      try {
        // Keep visible quantity +/- and deal cards in BOTH directions.
        // Values above available deal sizes are allowed as regular quantities,
        // without advertising an unconfigured additional discount.
        const radios = [...selector.querySelectorAll('input[type="radio"]')];
        radios.forEach(radio => {
          radio.checked = Number.parseInt(radio.value,10) === qty;
        });
        if (origin !== 'input' && String(liveQuantityInput.value) !== String(qty)) {
          liveQuantityInput.value = String(qty);
          liveQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          liveQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        submitText.textContent = qty === 1 ? defaultButtonText : `Add ${qty} to cart`;
        updateCards(unitCents);
        updateVisiblePrice(qty, unitCents);
        if (announce) {
          const status = selector.querySelector('[data-rgb-multibuy-status]');
          if (status) status.textContent = `${qty} ${qty > 1 ? productPlural : productLabel} selected. ` +
            (mode === 'shopify_cart_offer' ? 'Bundle savings calculated at cart.' : `Estimated total ${formatMoney(getTotalCents(qty, unitCents))}.`);
        }
      } finally {
        syncingQuantity = false;
      }
    };

    if (purchaseRow) {
      purchaseRow.insertAdjacentElement('beforebegin', selector);
      root.classList.add('rgb-multibuy-host--custom');
    } else if (genericBuyButtons) {
      quantityWrap.insertAdjacentElement('afterend', selector);
      root.classList.add('rgb-multibuy-host--generic');
    } else {
      return;
    }
    bridge.remove();
    // Keep the real Shopify quantity slider visible and tied to deal buttons.
    quantityWrap.classList.remove('rgb-multibuy-quantity--hidden');
    quantityWrap.hidden = false;
    root.addEventListener('change', (event) => {
      if (syncingQuantity || !event.target?.matches?.('.product-form__quantity input[name="quantity"]')) return;
      const qty = Number.parseInt(event.target.value, 10);
      if (Number.isSafeInteger(qty) && qty >= 1) applySelection(qty, true, 'input');
    });

    selector.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => radio.checked && applySelection(Number.parseInt(radio.value, 10)));
    });

    root.addEventListener('change', (event) => {
      if (event.target?.matches?.('.product-form__quantity input[name="quantity"]') || event.target?.closest?.('[data-rgb-multibuy]')) return;
      const updateVariant = () => {
        const qty = Number.parseInt(root.querySelector('.product-form__quantity input[name="quantity"]')?.value, 10) || selectedQty();
        applySelection(qty, false, 'variant');
      };
      window.setTimeout(updateVariant, 120);
      window.setTimeout(updateVariant, 320);
      window.setTimeout(updateVariant, 700);
    });

    // Dawn can asynchronously re-render the price DOM on a variant switch.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (data?.sectionId && root.dataset.section && String(data.sectionId) !== String(root.dataset.section)) return;
        window.requestAnimationFrame(() => {
          if (!root.isConnected) return;
          const qty = Number.parseInt(root.querySelector('.product-form__quantity input[name="quantity"]')?.value, 10) || selectedQty();
          applySelection(qty, false, 'variant');
        });
      });
    }

    applySelection(Number.parseInt(quantityInput.value, 10) || selectedQty(), false);
  };

  const init = () => document.querySelectorAll('[data-rgb-multibuy-bridge]').forEach(initBridge);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();